import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import PropTypes from 'prop-types';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { useContextualCanWrite } from '../../../abilities/hooks';
import { READ } from '../../../abilities/actions';
import ContextualCan from '../../../abilities/components/ContextualCan';
import { useTranslatedOptions } from '../../hooks';
import { withPrefix } from '../../utils';

const useStyles = makeStyles((theme) => ({
    formControlHorizontalFullWidth: {
        width: '100%',
    },
    radioGroupHorizontal: {
        flexDirection: 'row',
    },
    labelHorizontal: {
        marginRight: theme.spacing(4),
    },
}));

const BaseRadioInput = ({
    name,
    value,
    label,
    options: overrideOptions,
    translation,
    error,
    onChange,
    empty,
    disabled,
    horizontal,
    fullWidth,
    allowEmpty,
    prefix,
}) => {
    const prefixedName = withPrefix(name, prefix);
    const classes = useStyles();
    const canWrite = useContextualCanWrite(prefixedName);

    const options = useTranslatedOptions(name, overrideOptions, translation, allowEmpty);

    return (
        <ContextualCan I={READ} field={prefixedName}>
            <FormControl
                component="fieldset"
                className={classNames({
                    [classes.formControlHorizontalFullWidth]: horizontal && fullWidth,
                })}
            >
                {label && <FormLabel component="legend">{label}</FormLabel>}
                <RadioGroup
                    aria-label={prefixedName}
                    name={prefixedName}
                    value={value}
                    onChange={onChange}
                    className={classNames({ [classes.radioGroupHorizontal]: horizontal })}
                >
                    {empty && (
                        <FormControlLabel
                            value=""
                            control={<Radio color="default" />}
                            label={empty}
                            disabled={disabled || !canWrite}
                            className={classNames({ [classes.labelHorizontal]: horizontal })}
                        />
                    )}
                    {options.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio color="primary" />}
                            label={option.label}
                            disabled={disabled || !canWrite}
                            className={classNames({ [classes.labelHorizontal]: horizontal })}
                        />
                    ))}
                </RadioGroup>
                {error && <FormHelperText error>{error}</FormHelperText>}
            </FormControl>
        </ContextualCan>
    );
};

BaseRadioInput.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
    onChange: PropTypes.func,
    label: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
                .isRequired,
        })
    ),
    translation: PropTypes.string,
    error: PropTypes.string,
    empty: PropTypes.string,
    disabled: PropTypes.bool,
    horizontal: PropTypes.bool,
    fullWidth: PropTypes.bool,
    allowEmpty: PropTypes.bool,
    prefix: PropTypes.string,
};

BaseRadioInput.defaultProps = {
    options: null,
    translation: null,
    onChange: null,
    label: null,
    error: null,
    empty: null,
    disabled: false,
    horizontal: false,
    fullWidth: false,
    allowEmpty: false,
    prefix: null,
};

export default BaseRadioInput;
